
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null); 
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // First, set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth state changed:', event, session?.user?.id);
        setSession(session);
        setUser(session?.user || null);
        
        if (event === 'SIGNED_IN') {
          console.log('User signed in successfully');
        }
      }
    );

    // Then check current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('Current session check:', session?.user?.id);
      setSession(session);
      setUser(session?.user || null);
      setLoading(false);
    });

    // Cleanup subscription
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) throw error;
      
      console.log('Sign in successful:', data.user?.id);
      setLoading(false);
      return true;
    } catch (error: any) {
      console.error('Sign in error:', error.message);
      setLoading(false);
      throw error;
    }
  };

  const signUp = async (email: string, password: string, metadata?: object) => {
    setLoading(true);
    console.log("Signing up with metadata:", metadata);
    
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { 
          data: metadata,
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });
      
      if (error) {
        console.error("Signup error:", error);
        throw error;
      } else {
        console.log("Signup successful, user data:", data);
      }
      
      setLoading(false);
      return { user: data.user, error: null };
    } catch (e: any) {
      console.error("Unexpected error during signup:", e);
      setLoading(false);
      return { user: null, error: e };
    }
  };

  const signInWithGoogle = async () => {
    setLoading(true);
    try {
      console.log('Initiating Google sign in...');
      
      // Get the current URL origin for the redirect
      const redirectUrl = `${window.location.origin}/auth/callback`;
      console.log('Using redirect URL:', redirectUrl);
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });

      if (error) {
        console.error('Google OAuth error:', error);
        setLoading(false);
        throw error;
      }

      console.log('Google OAuth initiated successfully:', data);
      // No need to set loading to false as we're redirecting to Google
      return data;
    } catch (error: any) {
      console.error('Google sign in error:', error.message);
      setLoading(false);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      console.log('Sign out successful');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  return {
    user,
    session,
    loading,
    signIn,
    signUp,
    signInWithGoogle,
    signOut
  };
};
